import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createKnowledgeBase,
  deleteDocument,
  deleteKnowledgeBase,
  getDocumentInfo,
  getKnowledgeBase,
  getKnowledgeBases,
  getKnowledgeBaseStatus,
  queryKnowledgeBase,
  submitKnowledgeBaseFeedback,
  updateKnowledgeBase,
} from 'utils/funcs/knowledge-bases/knowledgeBase'

// Query keys for React Query
export const KNOWLEDGE_BASE_KEYS = {
  all: ['knowledgeBases'],
  lists: () => [...KNOWLEDGE_BASE_KEYS.all, 'list'],
  list: filters => [...KNOWLEDGE_BASE_KEYS.lists(), { filters }],
  details: () => [...KNOWLEDGE_BASE_KEYS.all, 'detail'],
  detail: id => [...KNOWLEDGE_BASE_KEYS.details(), id],
  documents: () => [...KNOWLEDGE_BASE_KEYS.all, 'documents'],
  document: id => [...KNOWLEDGE_BASE_KEYS.documents(), id],
  status: id => [...KNOWLEDGE_BASE_KEYS.all, 'status', id],
}

/**
 * Hook to fetch all knowledge bases with pagination
 * @param {object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.limit - Items per page
 * @param {object} options - React Query options
 */
export function useKnowledgeBases(params = {}, options = {}) {
  return useQuery({
    queryKey: KNOWLEDGE_BASE_KEYS.list(params),
    queryFn: () => getKnowledgeBases(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  })
}

/**
 * Hook to fetch a specific knowledge base
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @param {object} options - React Query options
 */
export function useKnowledgeBase(knowledgeBaseId, options = {}) {
  return useQuery({
    queryKey: KNOWLEDGE_BASE_KEYS.detail(knowledgeBaseId),
    queryFn: () => getKnowledgeBase(knowledgeBaseId),
    enabled: !!knowledgeBaseId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  })
}

/**
 * Hook to get document information
 * @param {string} documentId - Document ID
 * @param {object} options - React Query options
 */
export function useDocumentInfo(documentId, options = {}) {
  return useQuery({
    queryKey: KNOWLEDGE_BASE_KEYS.document(documentId),
    queryFn: () => getDocumentInfo(documentId),
    enabled: !!documentId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  })
}

/**
 * Hook to get knowledge base status with polling
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @param {object} options - React Query options
 */
export function useKnowledgeBaseStatus(knowledgeBaseId, options = {}) {
  return useQuery({
    queryKey: KNOWLEDGE_BASE_KEYS.status(knowledgeBaseId),
    queryFn: () => getKnowledgeBaseStatus(knowledgeBaseId),
    enabled: !!knowledgeBaseId,
    refetchInterval: (data) => {
      // Only poll if status is PROCESSING
      if (data?.data?.status === 'PROCESSING') {
        return 2000 // Poll every 2 seconds
      }
      return false // Stop polling if not processing
    },
    ...options,
  })
}

/**
 * Hook to create a new knowledge base
 * @param {object} options - Mutation options
 */
export function useCreateKnowledgeBase(options = {}) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createKnowledgeBase,
    onSuccess: (data) => {
      // Invalidate and refetch knowledge bases list
      queryClient.invalidateQueries({ queryKey: KNOWLEDGE_BASE_KEYS.lists() })

      // Call onSuccess if provided
      options.onSuccess?.(data)
    },
    onError: (error) => {
      console.error('Error creating knowledge base:', error)
      options.onError?.(error)
    },
    ...options,
  })
}

/**
 * Hook to update a knowledge base
 * @param {object} options - Mutation options
 */
export function useUpdateKnowledgeBase(options = {}) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ knowledgeBaseId, data }) => updateKnowledgeBase(knowledgeBaseId, data),
    onSuccess: (data, variables) => {
      // Update the specific KB in cache
      queryClient.setQueryData(
        KNOWLEDGE_BASE_KEYS.detail(variables.knowledgeBaseId),
        old => ({ ...old, data }),
      )

      // Invalidate lists to ensure consistency
      queryClient.invalidateQueries({ queryKey: KNOWLEDGE_BASE_KEYS.lists() })

      options.onSuccess?.(data, variables)
    },
    onError: (error) => {
      console.error('Error updating knowledge base:', error)
      options.onError?.(error)
    },
    ...options,
  })
}

/**
 * Hook to delete a knowledge base
 * @param {object} options - Mutation options
 */
export function useDeleteKnowledgeBase(options = {}) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteKnowledgeBase,
    onSuccess: (data, knowledgeBaseId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: KNOWLEDGE_BASE_KEYS.detail(knowledgeBaseId) })

      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: KNOWLEDGE_BASE_KEYS.lists() })

      options.onSuccess?.(data, knowledgeBaseId)
    },
    onError: (error) => {
      console.error('Error deleting knowledge base:', error)
      options.onError?.(error)
    },
    ...options,
  })
}

/**
 * Hook to query the knowledge base (for chat functionality)
 * @param {object} options - Mutation options
 */
export function useQueryKnowledgeBase(options = {}) {
  return useMutation({
    mutationFn: queryKnowledgeBase,
    onError: (error) => {
      console.error('Error querying knowledge base:', error)
      options.onError?.(error)
    },
    ...options,
  })
}

/**
 * Hook to submit feedback for KB responses
 * @param {object} options - Mutation options
 */
export function useSubmitKnowledgeBaseFeedback(options = {}) {
  return useMutation({
    mutationFn: submitKnowledgeBaseFeedback,
    onError: (error) => {
      console.error('Error submitting feedback:', error)
      options.onError?.(error)
    },
    ...options,
  })
}

/**
 * Hook to delete a document
 * @param {object} options - Mutation options
 */
export function useDeleteDocument(options = {}) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteDocument,
    onSuccess: (data, documentId) => {
      // Remove document from cache
      queryClient.removeQueries({ queryKey: KNOWLEDGE_BASE_KEYS.document(documentId) })

      // Invalidate KB lists since document counts may change
      queryClient.invalidateQueries({ queryKey: KNOWLEDGE_BASE_KEYS.lists() })

      options.onSuccess?.(data, documentId)
    },
    onError: (error) => {
      console.error('Error deleting document:', error)
      options.onError?.(error)
    },
    ...options,
  })
}

/**
 * Utility function to prefetch a knowledge base
 * @param {object} queryClient - React Query client
 * @param {string} knowledgeBaseId - Knowledge base ID
 */
export function prefetchKnowledgeBase(queryClient, knowledgeBaseId) {
  return queryClient.prefetchQuery({
    queryKey: KNOWLEDGE_BASE_KEYS.detail(knowledgeBaseId),
    queryFn: () => getKnowledgeBase(knowledgeBaseId),
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Utility function to invalidate all knowledge base queries
 * @param {object} queryClient - React Query client
 */
export function invalidateKnowledgeBaseQueries(queryClient) {
  queryClient.invalidateQueries({ queryKey: KNOWLEDGE_BASE_KEYS.all })
}
